import $ from 'jquery';

import doT, { RenderFunction } from 'bootstrap/load-dot';

import { Skin } from 'lib/vendor/terrific/Application';
import { initNewModules } from 'lib/util/helpers';

import LegoContainer, {
	toFloat,
} from '../Tc.Module.LegoContainer';

// tslint:disable-next-line: no-duplicate-imports
import type {
	Prices,
	Flavour,
	ProductCatalog,
	ServiceId,
	LegoContainerConfig,
	TrackingInfo,
} from '../Tc.Module.LegoContainer';

function getFlavour(flavourId : string, flavours : Flavour[]) {
	return flavours.find(flavour => flavour.siebelId === flavourId) ?? null;
}

interface Discount {
	amount : number;
	discountId : string;
	disclaimer : string;
	label : string;
}

interface AdvisorSummarySkin extends LegoContainer {
	conversionCategory : string;
	conversionDetail : string;
	conversionClass : string;
	discounts : Discount[];
	currentDiscounts : Discount[];
	prices : number;
	discountTemplate : RenderFunction;
	$actions : Record<string, JQuery<any>>;
	services : ServiceId[];

	getPricesFromCurrentSelection() : Prices;
	calculateTotalPrice() : Prices & Record<'monthly' | 'discount' | 'total', number>;
	clearDiscounts() : void;
	updateActionButtons() : void;
	isDiscountActive(discountId : string) : boolean;
	handleMultiPageStepChanged(event : JQueryEventObject) : void;
	getDiscountAmount(discount : Discount) : number;
	isService(flavourId : string) : LegoContainerConfig | null;
	isDiscount(discountId : string) : Discount | null;
	getTrackingConfig() : TrackingInfo;
	updateCurrentSelection() : void;
	setCurrentSelection(service : LegoContainerConfig) : void;
	updatePricesUi() : void;
	updateSummaryUi() : void;
	hideContainer(containerId? : string) : boolean;
}

// tslint:disable-next-line: variable-name
export const AdvisorSummary : Skin<LegoContainer, AdvisorSummarySkin> = function AdvisorSummary(parent) {

	/**
	 * Initialize the component
	 */
	this.initComponent = function() {
		parent.initComponent.call(this);
		this.sandbox.subscribe('LEGO_CONTAINER', this);
		this.conversionCategory = this.$ctx.data('conversionCategory');
		this.conversionDetail = this.$ctx.data('conversionDetail');
		this.conversionClass = this.$ctx.data('conversionClass');
		this.discounts = this.$ctx.data('discount-config') || [];
		this.currentDiscounts = [];
		this.prices = 0;
		this.discountTemplate = doT.template(this.$ctx.find('script.tmpl-discount').html());
		this.u26rebate = this.$ctx.data('u26-rebate');
	};

	/**
	 * Cache all ui elements
	 */
	this.cacheUi = function() {
		this.ui = {};
		this.ui.form = this.$ctx.closest('.modScForm');
		this.ui.error = this.$ctx.find('.error');
		this.ui.content = this.$ctx.find('.content');

		this.ui.radio = this.ui.form.find('[type="radio"]');

		this.ui.details = this.$ctx.find('.legoDetailContainer');

		this.ui.price = this.$ctx.find('.price');
		this.ui.priceMonthly = this.$ctx.find('.monthly-price');
		this.ui.monthlyAmount = this.$ctx.find('.monthly-price .amount');
		this.ui.totalAmount = this.$ctx.find('.total-price .amount');

		const $actionsMenu = this.$ctx.find('.actions');
		this.$actions = {};
		this.$actions.$normal = $actionsMenu.find('.normal');
		this.$actions.$u26 = $actionsMenu.find('.u26rebate');
		this.$actions.normalLink = this.$actions.$normal.find('.flat-button.secondary');
		this.$actions.normalOrder = this.$actions.$normal.find('.modLegoOrderButton .lego-order-button');
		this.$actions.u26lLink = this.$actions.$u26.find('.flat-button.secondary');
		this.$actions.u26Order = this.$actions.$u26.find('.modLegoOrderButton .lego-order-button');

		if (!this.isEditMode) {
			this.ui.details.addClass('hidden');
		}
	};

	/**
	 * Calculate all prices that are needed for the lego order button
	 * from the current selection.
	 */
	this.getPricesFromCurrentSelection = function() {
		const prices : Prices = {
			bundleBasePrice: this.config!.bundleBasePrice,
			homePrice: 0,
			mobilePrice: 0,
			totalPrice: this.config!.bundleBasePrice,
		};

		// Check for selected discounts
		$.each(this.currentDiscounts, function(_i, discount) {
			prices.totalPrice += toFloat(discount.amount);
		});

		$.each(this.currentSelection, (_i, item) => {
			if (item!.sliderId === 'mobile') {
				prices.mobilePrice = item!.cost;
			}
			prices.totalPrice += item!.cost;
		});

		prices.homePrice = prices.totalPrice - prices.mobilePrice;

		return prices;
	};

	/**
	 * Calculate the total price
	 */
	this.calculateTotalPrice = function() {
		const amount = {
			...parent.calculateTotalPrice.call(this, this.deepLinkConf?.config!),
			discount: 0,
			monthly: this.config!.bundleBasePrice,
			total: 0,
		};

		$.each(this.currentSelection, function(_serviceName, service) {
			amount.monthly += service!.cost;
		});

		// Check for selected discounts
		$.each(this.currentDiscounts, function(_index, discount) {
			amount.discount += toFloat(discount.amount);
		});

		amount.total = amount.monthly + amount.discount;

		return amount;
	};

	/**
	 * Clear any previously set discounts from the price ui
	 */
	this.clearDiscounts = function() {
		this.ui.price.find('.discount').remove();
	};

	this.updateActionButtons = function() {
		if(!this.u26rebate) {
			return;
		}

		this.$actions.$normal.toggleClass('hidden', this.isDiscountActive('u26PromoPriceDifference'));
		this.$actions.$u26.toggleClass('hidden', !this.isDiscountActive('u26PromoPriceDifference'));
	};

	this.isDiscountActive = function(discountId) {
		return this.currentDiscounts
			.map(discount => discount.discountId)
			.includes(discountId);
	};

	/**
	 * Overrides for the methods in base class that would
	 * cause trouble here if they weren't silenced.
	 */
	this.initUi = $.noop;
	this.handleScroll = $.noop;
	this.updateSummaryPosition = $.noop;

	this.addEventListeners = function() {
		this.$actions.normalLink.on('click', this.handleActionLink.bind(this));
		this.$actions.u26lLink.on('click', this.handleActionLink.bind(this));
		this.ui.form.on('stepchange', this.handleMultiPageStepChanged.bind(this));
		this.$actions.normalOrder.on('click', this.handleOrder.bind(this));
		this.$actions.u26Order.on('click', this.handleOrder.bind(this));
	};

	/**
	 * Retrieve a discount amount, either the one found in the data set
	 * loaded by a third-party environment or the one provided through the
	 * component dialog.
	 *
	 * Are both set, the third-party data shall precede.
	 */
	this.getDiscountAmount = function(discount) {
		const config = this.config ?? {} as any;
		if(config.hasOwnProperty(discount.discountId)) {
			return toFloat(config[discount.discountId] as number) ?? 0;
		}

		return discount.amount || 0;
	};

	/**
	 * Providing a flavour id, find the services this flavour belongs to.
	 */
	this.isService = function(flavourId) {
		for(const serviceId of this.services) {
			const service = this.config![serviceId]!;
			const flavour = getFlavour(flavourId, service.flavours);
			if(flavour) {
				return {sliderId: serviceId, slideId: flavourId, cost: flavour.priceDifference};
			}
		}

		return null;
	};

	/**
	 * Figure out if a provided id is a discount.
	 */
	this.isDiscount = function(discountId) {
		for(const discount of this.discounts) {
			if(discount.discountId && discount.discountId === discountId) {
				discount.amount = this.getDiscountAmount(discount);

				return discount;
			}
		}

		return null;
	};

	/**
	 * Generates a tracking object
	 */
	this.getTrackingConfig = function() {
		const selectedProducts : LegoContainerConfig[] = [];
		$.each(this.currentSelection, function(_key, value) {
			selectedProducts.push(value!);
		});

		return {
			conversionCategory: this.conversionCategory,
			conversionClass: this.conversionClass,
			conversionDetail: this.conversionDetail,
			selectedProducts,
		};
	};

	/**
	 * Event handler that is called after third-party data was loaded failed.
	 */
	this.handleDataFail = function() {
		if(!this.isEditMode) {
			this.ui.error.removeClass('hidden');
		}
	};

	/**
	 * Event handler that is called after third-party data was loaded successfully.
	 */
	this.handleDataSuccess = function() {
		this.ui.content.removeClass('hidden');

		if (!this.isEditMode) {
			// Filter all the services keys from the returned json.
			// use only the ones with a flavours array.
			const config : Partial<ProductCatalog> = this.config ?? {};
			this.services = (Object.keys(this.config!) as (keyof ProductCatalog)[])
				.filter(key => {
					if(!(key in config) || !config[key]) {
						return false;
					}

					const service = config[key];
					if(typeof service !== 'object') {
						return false;
					}

					return 'flavours' in service;
				}) as ServiceId[];

			// Go through all selected options and find the service
			this.updateCurrentSelection();
			this.updatePricesUi();
			this.updateSummaryUi();
			this.broadcastConfigurationChanged(this.currentSelection, this.getPricesFromCurrentSelection());


			if(this.conversionCategory && this.conversionDetail) {
				setTimeout(() => {
					this.track(this.getTrackingConfig());
				}, 500);
			}
		}
	};

	/**
	 * Update the summery every time a step changes
	 */
	this.handleMultiPageStepChanged = function(event) {
		this.log('event', event);
		// If the data load failed before, try to do that again.
		if(!this.services) {
			this.loadData();
		} else {
			// Reset the current selection, as it may have changed
			this.currentSelection = {};
			this.updateCurrentSelection();
			this.updatePricesUi();
			this.updateSummaryUi();
			this.broadcastConfigurationChanged(this.currentSelection, this.getPricesFromCurrentSelection());
		}
	};

	this.loadData = function() {
		if (!this.isEditMode) {
			this.ui.error.addClass('hidden');
			this.ui.content.addClass('hidden');
		}
		parent.loadData.call(this);
	};

	/**
	 * Check if a container id matches a currently selected flavour.
	 */
	this.hideContainer = function(containerId) {
		let hide = true;

		// If the container does not have any id set, we want to always display it.
		if(typeof containerId === 'undefined' || containerId.length === 0) {
			return false;
		}

		$.each(this.currentSelection, (_serviceName, service) => {
			if(containerId.indexOf(service!.slideId) > -1) {
				hide = false;

				return false; // Break $.each
			}

			return undefined;
		});

		return hide;
	};

	/**
	 * Update the current selection model with values from a new service
	 * But only if the services flavour has a higher price than the
	 * service that is store in the current selection with the same name.
	 */
	this.setCurrentSelection = function(service) {
		if(!this.currentSelection[service.sliderId] || this.currentSelection[service.sliderId]!.cost < service.cost) {
			this.currentSelection[service.sliderId] = service;
		}
	};

	/**
	 * Go through all checked radio buttons in the multi-step form.
	 * If the value matches a service in the data model set it as a
	 * current selection.
	 *
	 * If it's not a service check if it matches a discount in the discount
	 * model and store it in the current discounts if so.
	 */
	this.updateCurrentSelection = function() {
		const mod = this;
		this.currentDiscounts = [];

		$.each(this.ui.radio.filter(':checked'), function(_i, radio) {
			const service = mod.isService($(radio).val() as string);

			// It is a service...
			if(service) {
				// ... store in currentSelection
				mod.setCurrentSelection(service);

				// And continue;
				return;
			}

			// It's not a service. maybe it's a discount
			const discount = mod.isDiscount($(radio).val() as string);

			if(discount) {
				mod.currentDiscounts.push(discount);
			}
		});

		this.updateActionButtons();
		// Hard-code no mobile option so the deeplink to the
		// configurator will disable the mobile option.
		// According to Bettina Breiter the advisor will NEVER
		// contain mobile options
		this.currentSelection.mobile = {sliderId: 'mobile', slideId: 'N_LEGO No Mobile', cost: 0};
	};

	/**
	 * Update the price ui with monthly prices, total prices and discounts.
	 */
	this.updatePricesUi = function() {
		const amount = this.calculateTotalPrice();
		this.ui.monthlyAmount.text(SCS.utils.formatPriceWithDash(amount.monthly));

		this.ui.totalAmount.text(SCS.utils.formatPriceWithDash(amount.total));

		this.clearDiscounts();
		const mod = this;
		let discounts = '';
		$.each(this.currentDiscounts, function(_i, discount) {
			discounts += mod.discountTemplate({
				amount: SCS.utils.formatPriceWithDash(discount.amount),
				disclaimer: discount.disclaimer,
				hasDisclaimer: (discount.disclaimer.length > 0),
				label: discount.label,
			});
		});

		this.ui.priceMonthly.after(discounts);
		initNewModules(this.ui.price);
	};

	/**
	 * Update the summary user interface.
	 */
	this.updateSummaryUi = function() {
		const mod = this;
		this.ui.details.each(function() {
			const $container = $(this);
			const id = $container.data('id');

			$(this).toggleClass('hidden', mod.hideContainer(id));
		});
	};

};



// WEBPACK FOOTER //
// typescript://../clientlibs/js/lib/ecommerce/lego/skins/AdvisorSummary.ts